/**
 * @fileoverview The share picture's layouts that are more than a photograph
 * filling the frame: the collage and its tiles, the card on its ground, and
 * Before & after's band and seam. Moved out of plant-list/lib/shareFrames
 * (batch 18, A110); nothing in it changed.
 *
 * Where: the build (features/share/frames/buildFrames) and the share studio's
 * collage taps (collageTiles).
 * Settings: SHARE_COLLAGE, SHARE_COLLAGE_TEMPLATES, SHARE_SIGNATURE,
 * SHARE_TALL_RATIO (config/tunables/plantList); Grow settings sharing.GROUNDS.
 *
 * @module frameLayouts
 */

import type { PlantPhoto } from '@/shared/types/photo';
import { SHARE_COLLAGE, SHARE_COLLAGE_TEMPLATES, SHARE_SIGNATURE, SHARE_TALL_RATIO } from '@/config/tunables/plantList';
import { BODY, SERIF, daysBetween, drawCover, pillPath, roundRect } from './canvasKit';
import { SHARE_SAFE, SHARE_SIZES, TEXT_BASE, groundColours, type ShareFrameInput, type ShareSize } from './frameOptions';
import { loadPhoto } from './framePhotos';
import { aspectOfRect, cardBox, cardSlot, halfSlot } from './slotShape';
import { ON_CARD, drawHandle, drawSignature, drawTextBlock, planText, signs } from './frameText';

/**
 * The collage's tiles for this many photographs, in pixels, largest first:
 * the arrangement for the count (SHARE_COLLAGE_TEMPLATES), inside the border,
 * each cut back by half the gap on its inner sides.
 *
 * @param count - How many photographs (2 to 9; more use 9's).
 * @param W - The frame's width.
 * @param H - The frame's height.
 * @param U - The frame's unit (its width over 1080).
 * @returns One rectangle per photograph, the largest first.
 */
export function collageTiles(count: number, W: number, H: number, U: number): Array<{ x: number; y: number; w: number; h: number }> {
  const template = SHARE_COLLAGE_TEMPLATES[Math.min(9, Math.max(2, count))] ?? SHARE_COLLAGE_TEMPLATES[9];
  const pad = SHARE_COLLAGE.padPx * U;
  const half = (SHARE_COLLAGE.gapPx * U) / 2;
  const iw = W - 2 * pad;
  const ih = H - 2 * pad - Math.max(0, SHARE_COLLAGE.footPx * U - pad);
  const edge = 0.001;
  return template
    .map(([fx, fy, fw, fh]) => {
      const left = fx <= edge ? 0 : half;
      const top = fy <= edge ? 0 : half;
      const right = fx + fw >= 1 - edge ? 0 : half;
      const bottom = fy + fh >= 1 - edge ? 0 : half;
      return {
        x: Math.round(pad + fx * iw + left),
        y: Math.round(pad + fy * ih + top),
        w: Math.round(fw * iw - left - right),
        h: Math.round(fh * ih - top - bottom),
      };
    })
    .sort((a, b) => b.w * b.h - a.w * a.h);
}

/**
 * The collage picture: the photographs on the border colour, the first in
 * the largest tile, and the small credit in the corner one.
 *
 * @param ctx - The frame's canvas.
 * @param input - The sharer's choices.
 * @param W - The frame's width.
 * @param H - The frame's height.
 * @param U - The frame's unit.
 */
export async function drawCollage(ctx: CanvasRenderingContext2D, input: ShareFrameInput, W: number, H: number, U: number): Promise<void> {
  ctx.fillStyle = SHARE_COLLAGE.ground;
  ctx.fillRect(0, 0, W, H);
  const photos = input.photos.slice(0, 9);
  const tiles = collageTiles(photos.length, W, H, U);
  const radius = Math.round(SHARE_COLLAGE.radiusPx * U);
  for (const [index, photo] of photos.entries()) {
    const tile = tiles[index];
    if (!tile) break;
    const image = await loadPhoto(input, photo.photo_id);
    ctx.save();
    roundRect(ctx, tile.x, tile.y, tile.w, tile.h, radius);
    ctx.clip();
    drawCover(ctx, image, tile.x, tile.y, tile.w, tile.h, input.focusFor(photo));
    ctx.restore();
    image.close();
  }
  // The address in the bottom border, right-aligned with the photographs.
  if (signs(input, 0)) {
    const pad = SHARE_COLLAGE.padPx * U;
    const foot = Math.max(pad, SHARE_COLLAGE.footPx * U);
    drawSignature(ctx, input.address as string, W - pad, H - foot / 2 + (SHARE_SIGNATURE.sizePx * U) / 3, U, '#6b645a');
  }
}

/**
 * The card: the photograph inside a cream card with a margin, tilted a few
 * degrees, on a plain ground of the gardener's choosing (garden green unless
 * they change it), the text beneath the picture.
 *
 * Three of the reference's four layouts are a card object. A plant
 * photograph often has a busy ground that fights lettering; the card takes
 * the lettering off the photograph entirely, and it is the layout that most
 * plainly says "shared from somewhere".
 *
 * @param ctx - The frame's canvas.
 * @param input - The sharer's choices.
 * @param photo - The photograph on the card.
 * @param W - The frame's width.
 * @param H - The frame's height.
 * @param U - The frame's unit (its width over 1080).
 * @param signed - This picture carries the signature (the set's first).
 */
export async function drawCardLayout(
  ctx: CanvasRenderingContext2D, input: ShareFrameInput, photo: PlantPhoto,
  W: number, H: number, U: number, signed = false
): Promise<void> {
  const ground = groundColours(input.cardGround ?? 'green');
  ctx.fillStyle = ground.fill;
  ctx.fillRect(0, 0, W, H);

  const tall = SHARE_SIZES[input.size].h / SHARE_SIZES[input.size].w >= SHARE_TALL_RATIO;
  const { cardW, pad, inner, textPad } = cardBox(W, tall);
  const T = U * TEXT_BASE * (input.textScale ?? 1);
  const plan = planText(ctx, input, photo, inner, T);
  const { photoH, cardH } = cardSlot(W, H, tall, plan.height);

  const image = await loadPhoto(input, photo.photo_id);
  ctx.save();
  ctx.translate(W / 2, H / 2);
  ctx.rotate(-3 * Math.PI / 180);
  const x = -cardW / 2;
  const y = -cardH / 2;
  ctx.shadowColor = 'rgba(0,0,0,0.35)';
  ctx.shadowBlur = Math.round(40 * U);
  ctx.shadowOffsetY = Math.round(12 * U);
  roundRect(ctx, x, y, cardW, cardH, Math.round(10 * U));
  ctx.fillStyle = '#FBF8F1';
  ctx.fill();
  ctx.shadowColor = 'transparent';
  ctx.save();
  roundRect(ctx, x, y, cardW, cardH, Math.round(10 * U));
  ctx.clip();
  drawCover(ctx, image, x, y, cardW, photoH, input.focusFor(photo));
  ctx.restore();
  drawTextBlock(ctx, plan, x + pad, y + photoH + textPad, T, ON_CARD);
  ctx.restore();
  image.close();

  const margin = Math.round(64 * U);
  drawHandle(ctx, input, margin, Math.round(H * SHARE_SAFE[input.size].top), T, ground.ink, photo);
  if (signed) {
    const inset = Math.round(SHARE_SIGNATURE.insetPx * U);
    drawSignature(ctx, input.address as string, W - inset, H - Math.round(H * SHARE_SAFE[input.size].bottom) - inset, U, ground.ink);
  }
}

/**
 * The two dates and the days between them, as one band across the seam.
 *
 * One fact, so it is set as one thing. Stamped into the corner of each panel
 * the first date landed on the gardener's handle, and a reader had to assemble
 * the comparison from three separate places.
 *
 * @param ctx - The frame's canvas.
 * @param input - The sharer's choices.
 * @param first - The earlier photograph.
 * @param last - The later one.
 * @param W - The frame's width.
 * @param H - The frame's height.
 * @param U - The lettering's unit.
 */
export function drawSpanBand(
  ctx: CanvasRenderingContext2D, input: ShareFrameInput,
  first: PlantPhoto, last: PlantPhoto, W: number, H: number, U: number
): void {
  const span = input.formatSpan(daysBetween(first.date_taken, last.date_taken));
  const showDates = input.elements.date;
  const from = showDates && first.date_taken ? input.formatDate(first.date_taken) : '';
  const to = showDates && last.date_taken ? input.formatDate(last.date_taken) : '';

  const spanFont = `700 ${Math.round(64 * U)}px ${SERIF}`;
  const whenFont = `400 ${Math.round(36 * U)}px ${BODY}`;
  const gap = Math.round(22 * U);

  ctx.font = whenFont;
  const fromW = from ? ctx.measureText(from).width : 0;
  const toW = to ? ctx.measureText(to).width : 0;
  ctx.font = spanFont;
  const spanW = ctx.measureText(span).width;

  const total = spanW + (from ? fromW + gap : 0) + (to ? toW + gap : 0);
  let cursor = (W - total) / 2;
  const baseline = H / 2 + Math.round(18 * U);

  /*
   * A dark pill behind the band, rather than a drop shadow alone.
   *
   * White lettering on a shadow is legible over soil and illegible over a
   * sunlit lawn, which is exactly what a before-and-after of a squash bed is
   * half made of. The pill is the one deliberate chip on the frame, and it is
   * spent on the number only Grow can write.
   */
  const padX = Math.round(30 * U);
  const padY = Math.round(22 * U);
  const boxH = Math.round(52 * U) + padY * 2;
  const boxW = total + padX * 2;
  const boxX = (W - boxW) / 2;
  const boxY = baseline - Math.round(52 * U) - padY + Math.round(6 * U);
  pillPath(ctx, boxX, boxY, boxW, boxH);
  ctx.fillStyle = 'rgba(16,20,14,0.55)';
  ctx.fill();

  ctx.save();
  ctx.textAlign = 'left';

  if (from) {
    ctx.font = whenFont;
    ctx.fillStyle = 'rgba(255,255,255,0.92)';
    ctx.fillText(from, cursor, baseline);
    cursor += fromW + gap;
  }
  ctx.font = spanFont;
  ctx.fillStyle = '#ffffff';
  ctx.fillText(span, cursor, baseline);
  cursor += spanW + gap;
  if (to) {
    ctx.font = whenFont;
    ctx.fillStyle = 'rgba(255,255,255,0.92)';
    ctx.fillText(to, cursor, baseline);
  }
  ctx.restore();
}

/**
 * Where the before-and-after seam runs: across a tall frame, down a square.
 *
 * @param size - The frame's size.
 * @returns True when the two photographs stack, one above the other.
 */
export function stacks(size: ShareSize): boolean {
  const { w, h } = SHARE_SIZES[size];
  return h / w >= SHARE_TALL_RATIO;
}

/**
 * The width over the height of the slot a photograph fills in the picture the
 * chosen size and layout make (B86): what the crop box must be, so cropping a
 * square gives a square. The whole frame for the single-photograph layouts, the
 * photo area of the card (which the lettering's height decides), a half for
 * Before & after. A collage tile has its own shape (collageTiles).
 *
 * @param input - The sharer's choices.
 * @param photo - The photograph being cropped (the card's lettering depends on it).
 * @returns The slot's width over its height.
 */
export function slotAspectOf(input: ShareFrameInput, photo: PlantPhoto): number {
  const { w: W, h: H } = SHARE_SIZES[input.size];
  const tall = stacks(input.size);
  if (input.layout === 'ba') return aspectOfRect(halfSlot(W, H, tall));
  if (input.layout !== 'card') return aspectOfRect({ w: W, h: H });
  const ctx = typeof document === 'undefined' ? null : document.createElement('canvas').getContext('2d');
  if (!ctx) return aspectOfRect({ w: W, h: H });
  const U = W / 1080;
  const plan = planText(ctx, input, photo, cardBox(W, tall).inner, U * TEXT_BASE * (input.textScale ?? 1));
  const slot = cardSlot(W, H, tall, plan.height);
  return aspectOfRect({ w: slot.cardW, h: slot.photoH });
}
